import React, { createContext, useContext, ReactNode, useEffect, useState, useCallback } from 'react';
import { useBleDeviceMonitor } from 'web/hooks/useBleDeviceMonitor';
import { StoredDevice } from 'web/types/nxStoreDevice';

// Define the SyncStatus type
export interface SyncStatus {
  isAutoSyncing: boolean;
  syncStage: string;
  lastSyncTime?: number;
  syncProgress?: number;
  filesTransferred?: number;
}

interface DeviceMonitoringContextType {
  devices: StoredDevice[];
  monitoredDeviceStatuses: Record<string, boolean>;
  isActiveRecording: Record<string, boolean>;
  isDeviceMonitored: (deviceId: string) => boolean;
  isDeviceConnected: (deviceId: string) => boolean;
  getConnectionStatusDetails: (deviceId: string) => {
    status: string;
    statusText: string;
    lastConnectionAttempt?: number;
    reconnectionAttempts: number;
  };
  notification: {
    message: string;
    type: 'error' | 'warning' | 'success' | 'info';
    timestamp: number;
    deviceId?: string;
    deviceName?: string;
  } | null;
  syncStatus: Record<string, SyncStatus>;
  startMonitoring: (device: BluetoothDevice, organizationId: string, patientId?: string) => Promise<void>;
  stopMonitoring: (device: BluetoothDevice) => Promise<void>;
  refreshDevices: () => void;
}

const DeviceMonitoringContext = createContext<DeviceMonitoringContextType | undefined>(undefined);

export const DeviceMonitoringProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Use state instead of just useLocalStorage to have more control over when we reload
  const [devices, setDevices] = useState<StoredDevice[]>([]);
  const [_ , setIsInitialized] = useState(false);

  // Load devices from localStorage
  const reloadDevicesFromStorage = useCallback(() => {
    try {
      const storedDevicesJson = localStorage.getItem('pairedDevices');
      const storedDevices: StoredDevice[] = storedDevicesJson ? JSON.parse(storedDevicesJson) : [];
      setDevices(storedDevices);
      console.log('Reloaded paired devices from localStorage:', storedDevices.length);
    } catch (error) {
      console.error('Error loading devices from localStorage:', error);
    }
  }, []);
  
  // Reload on initial mount
  useEffect(() => {
    reloadDevicesFromStorage();
  }, [reloadDevicesFromStorage]);

  const {
    notification,
    monitoredDeviceStatuses,
    isActiveRecording,
    isDeviceMonitored,
    isDeviceConnected,
    getConnectionStatusDetails,
    startMonitoring,
    stopMonitoring,
    restoreMonitoredDevices,
    syncStatus,
  } = useBleDeviceMonitor();

  // Attempt to reconnect to monitored devices on initialization
  useEffect(() => {
    const reconnectToSavedDevices = async () => {
      try {
        // Load all monitoring info from localStorage
        const monitoredDeviceInfo: Record<string, { deviceName: string, organizationId: string, patientId?: string }> = {};

        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('monitoring-')) {
            try {
              const deviceId = key.replace('monitoring-', '');
              const data = JSON.parse(localStorage.getItem(key) || '{}');

              if (data.deviceName && data.organizationId) {
                monitoredDeviceInfo[deviceId] = {
                  deviceName: data.deviceName,
                  organizationId: data.organizationId,
                  patientId: data.patientId
                };
              }
            } catch (error) {
              console.error('Error parsing monitoring info from localStorage:', error);
            }
          }
        }

        // Reload devices from localStorage to ensure we have the latest
        reloadDevicesFromStorage();
        
        // Restore monitored devices state from localStorage
        await restoreMonitoredDevices(monitoredDeviceInfo);
        console.log('Restored monitored devices from localStorage');
      } catch (error) {
        console.error('Failed to reconnect to saved devices:', error);
      } finally {
        setIsInitialized(true);
      }
    };

    reconnectToSavedDevices();
  }, [restoreMonitoredDevices, reloadDevicesFromStorage]);

  // Also set up event listener for storage changes (in case another tab updates localStorage)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'pairedDevices') {
        reloadDevicesFromStorage();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [reloadDevicesFromStorage]);

  // The refreshDevices function will be exposed to allow components to force a refresh
  const refreshDevices = useCallback(() => {
    reloadDevicesFromStorage();
  }, [reloadDevicesFromStorage]);

  return (
    <DeviceMonitoringContext.Provider
      value={{
        devices,
        monitoredDeviceStatuses,
        isActiveRecording,
        isDeviceMonitored,
        isDeviceConnected,
        getConnectionStatusDetails,
        notification,
        startMonitoring,
        stopMonitoring,
        refreshDevices,
        syncStatus,
      }}
    >
      {children}
    </DeviceMonitoringContext.Provider>
  );
};

export const useDeviceMonitoring = (): DeviceMonitoringContextType => {
  const context = useContext(DeviceMonitoringContext);
  if (context === undefined) {
    throw new Error('useDeviceMonitoring must be used within a DeviceMonitoringProvider');
  }
  return context;
}; 